import { useState } from "react";
import { AlertCircle, Copy, Send, Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = '/api';

export const ActivityBox = ({ 
  logs = [], 
  onAddLog, 
  onClearLogs,
  userEmail = "",
  sectionName = "Activity" 
}) => {
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportSending, setReportSending] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState("");

  const hasErrors = logs.some(entry => entry.type === 'error');

  const sendIssueReport = async () => {
    setReportSending(true);
    try {
      const logText = logs.map(entry => 
        `[${entry.timestamp}] ${entry.type.toUpperCase()}: ${entry.message}${entry.details ? '\n  Details: ' + entry.details : ''}`
      ).join('\n\n');

      const response = await axios.post(`${API}/report-issue`, {
        user_email: userEmail,
        activity_log: `[Section: ${sectionName}]\n\n${logText}`,
        error_type: logs.find(e => e.type === 'error')?.message || 'General Issue',
        browser_info: navigator.userAgent,
        additional_info: additionalInfo
      });

      if (response.data.success) {
        toast.success("Issue reported! We'll look into it.");
        setShowReportDialog(false);
        setAdditionalInfo("");
      } else {
        toast.error("Failed to send report: " + (response.data.error || 'Unknown error'));
      }
    } catch (err) {
      toast.error("Failed to send report: " + err.message);
    }
    setReportSending(false);
  };

  const copyLogs = () => {
    if (logs.length === 0) {
      toast.info("Nothing to copy");
      return;
    }
    const logText = logs.map(entry => 
      `[${entry.timestamp}] ${entry.type.toUpperCase()}: ${entry.message}${entry.details ? '\n  ' + entry.details : ''}`
    ).join('\n\n');
    navigator.clipboard.writeText(logText).then(() => {
      toast.success("Activity log copied!");
    }).catch(() => {
      toast.error("Failed to copy");
    });
  };

  return (
    <>
      <Card className="border-border bg-card/80 shadow-sm" data-testid="activity-box">
        <CardHeader className="py-2 px-4 border-b border-border">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-muted-foreground" />
              {sectionName} Activity
              {logs.length > 0 && (
                <span className="text-xs bg-secondary px-1.5 py-0.5 rounded-full text-muted-foreground">
                  {logs.length}
                </span>
              )}
            </CardTitle>
            <div className="flex items-center gap-1">
              {hasErrors && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowReportDialog(true)}
                  className="text-xs h-7 px-2"
                  data-testid="report-issue-btn"
                >
                  <Send className="w-3 h-3 mr-1" />
                  Report Issue
                </Button>
              )}
              {!hasErrors && logs.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowReportDialog(true)}
                  className="text-xs h-7 px-2 text-muted-foreground"
                  data-testid="send-report-btn"
                >
                  <Send className="w-3 h-3 mr-1" />
                  Send Report
                </Button>
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={copyLogs}
                className="text-xs h-7 px-2 text-muted-foreground"
              >
                <Copy className="w-3 h-3 mr-1" />
                Copy
              </Button>
              {logs.length > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={onClearLogs}
                  className="text-xs h-7 px-2 text-muted-foreground"
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="py-2 px-4 max-h-36 overflow-y-auto">
          {logs.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-3">
              Activity will appear here during operations. You can send a report anytime.
            </p>
          ) : (
            <div className="space-y-1.5">
              {logs.slice(0, 20).map((entry) => (
                <div 
                  key={entry.id} 
                  className={`text-xs p-2 rounded ${
                    entry.type === 'error' ? 'bg-red-900/30 border border-red-500/50' :
                    entry.type === 'success' ? 'bg-green-900/30 border border-green-500/50' :
                    entry.type === 'progress' ? 'bg-blue-900/30 border border-blue-500/50' :
                    entry.type === 'warning' ? 'bg-slate-800/40 border border-cyan-500/30' :
                    'bg-secondary border border-border'
                  }`}
                >
                  <div className="flex items-center justify-between mb-0.5">
                    <span className={`font-medium uppercase text-[10px] ${
                      entry.type === 'error' ? 'text-red-400' :
                      entry.type === 'success' ? 'text-green-400' :
                      entry.type === 'progress' ? 'text-blue-400' :
                      entry.type === 'warning' ? 'text-cyan-400' :
                      'text-muted-foreground'
                    }`}>
                      {entry.type}
                    </span>
                    <span className="text-muted-foreground text-[10px]">{entry.timestamp}</span>
                  </div>
                  <p className="text-foreground">{entry.message}</p>
                  {entry.details && (
                    <p className="mt-0.5 text-muted-foreground text-[10px]">{entry.details}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              <Send className="w-5 h-5" />
              Send Report - {sectionName}
            </DialogTitle>
            <DialogDescription>
              Send this activity report to help us improve your experience.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-3">
            {/* Error summary if errors exist */}
            {hasErrors && (
              <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-3">
                <h4 className="text-sm font-medium text-red-400 mb-1">Errors Found:</h4>
                <div className="space-y-1 max-h-24 overflow-y-auto">
                  {logs.filter(e => e.type === 'error').slice(0, 5).map((entry, i) => (
                    <p key={i} className="text-xs text-red-300">{entry.message}</p>
                  ))}
                </div>
              </div>
            )}

            {/* Activity summary */}
            <div className="bg-secondary border border-border rounded-lg p-3">
              <h4 className="text-sm font-medium text-foreground mb-1">Activity Summary:</h4>
              <p className="text-xs text-muted-foreground">
                {logs.length} entries will be included in this report.
              </p>
            </div>

            {/* Additional info */}
            <div>
              <label className="text-sm font-medium text-foreground">Additional Information (optional)</label>
              <Textarea
                placeholder="Describe what you were trying to do when the issue occurred..."
                value={additionalInfo}
                onChange={(e) => setAdditionalInfo(e.target.value)}
                className="mt-1 h-20"
              />
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowReportDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={sendIssueReport}
              disabled={reportSending}
              className="bg-cyan-500 hover:bg-cyan-600 text-black"
            >
              {reportSending ? "Sending..." : "Send Report"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Hook to manage activity logs
export const useActivityLogs = () => {
  const [logs, setLogs] = useState([]);

  const addLog = (type, message, details = null) => {
    const entry = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      type,
      message,
      details,
      timestamp: new Date().toLocaleTimeString()
    };
    setLogs(prev => [entry, ...prev].slice(0, 50));
    return entry;
  };

  const clearLogs = () => setLogs([]);

  return { logs, addLog, clearLogs };
};

export default ActivityBox;
